/**
 * Floating "Kundenportal-App" phone overlay for the Portal module. A bespoke
 * compact device (48×21 island, custom iOS status bar, bottom tab bar) that
 * sits at the bottom-right of the app window. Styling is 1:1 with the prototype
 * (Hausverwaltung Hero.dc.html, lines 1916–2004).
 */
import { ld } from "@/lib/theme/light-dark";
import {
  FileText,
  Folder,
  Inbox,
  LayoutGrid,
  List,
  Megaphone,
} from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";
import { StoreBadges } from "./store-logos";

/** One document tile thumbnail (CSS-drawn PDF page) + filename footer. */
function DocTile(props: {
  readonly variant: "table" | "lines";
  readonly headWidth: string;
  readonly label: string;
}): ReactNode {
  const t = useTranslations("marketing.modulePortalPhone");
  return (
    <div
      style={{
        border: `1px solid ${ld("#E9EBEF", "#313438")}`,
        borderRadius: 10,
        overflow: "hidden",
        background: ld("#fff", "#181b1f"),
      }}
    >
      <div
        style={{
          position: "relative",
          height: 80,
          background: ld("#F0F1F4", "#2b2e32"),
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 9,
            right: 9,
            height: 130,
            background: ld("#fff", "#181b1f"),
            border: `1px solid ${ld("#E6E8EB", "#33373b")}`,
            borderRadius: "3px 3px 0 0",
            boxShadow: `0 3px 8px -6px ${ld("rgba(11,15,25,0.2)", "rgba(0,0,0,0.35)")}`,
            padding: 7,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: props.variant === "lines" ? 3.5 : undefined,
          }}
        >
          <div
            style={{
              width: props.headWidth,
              height: 4,
              borderRadius: 2,
              background: ld("#C3C8CF", "#505357"),
              marginBottom: props.variant === "table" ? 5 : undefined,
            }}
          />
          {props.variant === "table" ? (
            <>
              <div
                style={{ display: "flex", borderBottom: `1px solid ${ld("#E3E6EA", "#35383c")}` }}
              >
                <div
                  style={{
                    flex: 2,
                    height: 7,
                    background: ld("#E0E4E9", "#373a3e"),
                    borderRight: `1px solid ${ld("#fff", "#181b1f")}`,
                  }}
                />
                <div
                  style={{
                    flex: 1,
                    height: 7,
                    background: ld("#E0E4E9", "#373a3e"),
                    borderRight: `1px solid ${ld("#fff", "#181b1f")}`,
                  }}
                />
                <div style={{ flex: 1, height: 7, background: ld("#E0E4E9", "#373a3e") }} />
              </div>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{ display: "flex", borderBottom: `1px solid ${ld("#EEF0F2", "#2d3034")}` }}
                >
                  <div
                    style={{
                      flex: 2,
                      height: 6,
                      borderRight: `1px solid ${ld("#F2F4F6", "#292c30")}`,
                    }}
                  />
                  <div
                    style={{
                      flex: 1,
                      height: 6,
                      borderRight: `1px solid ${ld("#F2F4F6", "#292c30")}`,
                    }}
                  />
                  <div style={{ flex: 1, height: 6 }} />
                </div>
              ))}
              <div style={{ display: "flex" }}>
                <div
                  style={{
                    flex: 2,
                    height: 6,
                    borderRight: `1px solid ${ld("#F2F4F6", "#292c30")}`,
                  }}
                />
                <div
                  style={{
                    flex: 1,
                    height: 6,
                    borderRight: `1px solid ${ld("#F2F4F6", "#292c30")}`,
                  }}
                />
                <div style={{ flex: 1, height: 6 }} />
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  width: "100%",
                  height: 2.5,
                  borderRadius: 2,
                  background: ld("#E9EBEE", "#313438"),
                  marginTop: 3,
                }}
              />
              <div
                style={{
                  width: "95%",
                  height: 2.5,
                  borderRadius: 2,
                  background: ld("#E9EBEE", "#313438"),
                }}
              />
              <div
                style={{
                  width: "98%",
                  height: 2.5,
                  borderRadius: 2,
                  background: ld("#E9EBEE", "#313438"),
                }}
              />
              <div
                style={{
                  width: "90%",
                  height: 2.5,
                  borderRadius: 2,
                  background: ld("#E9EBEE", "#313438"),
                }}
              />
              <div
                style={{
                  width: "94%",
                  height: 2.5,
                  borderRadius: 2,
                  background: ld("#E9EBEE", "#313438"),
                }}
              />
            </>
          )}
        </div>
        <span
          style={{
            position: "absolute",
            top: 7,
            right: 9,
            fontFamily: H.label,
            fontSize: 8,
            fontWeight: 700,
            color: ld("#D8443E", "#fb8276"),
          }}
        >
          {t("pdf")}
        </span>
      </div>
      <div
        style={{
          padding: "7px 8px",
          display: "flex",
          alignItems: "center",
          gap: 5,
          borderTop: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
        }}
      >
        <span
          style={{ color: ld("#D8443E", "#fb8276"), display: "inline-flex", flexShrink: 0 }}
        >
          <FileText width={12} height={12} strokeWidth={1.8} />
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: H.ink,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {props.label}
        </span>
      </div>
    </div>
  );
}

/** Bottom tab-bar item (active tab gets the accent pill behind the glyph). */
function TabItem(props: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly active?: boolean;
}): ReactNode {
  const active = props.active === true;
  return (
    <span
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        color: active ? H.accent : ld("rgba(11,15,25,0.42)", "rgba(250,250,250,0.42)"),
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 44,
          height: 28,
          borderRadius: 14,
          background: active ? H.accentSoft : "transparent",
          flexShrink: 0,
        }}
      >
        {props.icon}
      </span>
      <span
        style={{
          fontSize: 10,
          fontWeight: active ? 700 : 600,
          lineHeight: 1,
          whiteSpace: "nowrap",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {props.label}
      </span>
    </span>
  );
}

/** The floating Kundenportal-App phone overlay (bottom-right of the window). */
export function PortalPhoneOverlay(): ReactNode {
  const t = useTranslations("marketing.modulePortalPhone");
  return (
    <>
    <StoreBadges
      app="portal"
      style={{ position: "absolute", right: 280, bottom: -20, zIndex: 35 }}
    />
    <div
      style={{
        position: "absolute",
        right: 16,
        bottom: -44,
        zIndex: 30,
        width: 244,
      }}
    >
      <div
        style={{
          position: "relative",
          background: "linear-gradient(150deg,#43464D 0%,#0C0E12 56%)",
          borderRadius: 44,
          padding: 5,
          boxShadow:
            `0 44px 72px -32px ${ld("rgba(11,15,25,0.44)", "rgba(0,0,0,0.71)")}, 0 18px 34px -20px ${ld("rgba(11,15,25,0.3)", "rgba(0,0,0,0.5)")}, inset 0 2px 1.5px ${ld("rgba(255,255,255,0.18)", "rgba(255,255,255,0.06)")}, inset 0 0 0 1.5px rgba(255,255,255,0.06)`,
        }}
      >
        <div
          style={{
            position: "relative",
            background: ld("#fff", "#181b1f"),
            borderRadius: 38,
            overflow: "hidden",
            height: 510,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 11,
              left: "calc(50% - 24px)",
              width: 48,
              height: 21,
              background: "#000",
              borderRadius: 12,
              zIndex: 8,
            }}
          />
          {/* Status bar */}
          <div
            style={{
              flexShrink: 0,
              height: 46,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 13px",
              background: ld("#fff", "#181b1f"),
            }}
          >
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "#000",
                letterSpacing: "-0.02em",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {t("k941")}
            </span>
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <svg
                width="17"
                height="12"
                viewBox="0 0 17 12"
                fill="#000"
                style={{ display: "block" }}
                aria-hidden="true"
              >
                <rect x="0" y="7.5" width="3" height="4.5" rx="1" />
                <rect x="4.67" y="5" width="3" height="7" rx="1" />
                <rect x="9.33" y="2.5" width="3" height="9.5" rx="1" />
                <rect x="14" y="0" width="3" height="12" rx="1" />
              </svg>
              <svg
                width="16"
                height="12"
                viewBox="0 0 16 12"
                fill="none"
                stroke="#000"
                strokeWidth="1.55"
                strokeLinecap="round"
                style={{ display: "block" }}
                aria-hidden="true"
              >
                <path d="M1.72 5.23 A8.2 8.2 0 0 1 14.28 5.23" />
                <path d="M4.63 7.67 A4.4 4.4 0 0 1 11.37 7.67" />
                <circle cx="8" cy="10" r="1" fill="#000" stroke="none" />
              </svg>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <span
                  style={{
                    position: "relative",
                    width: 23,
                    height: 11.5,
                    border: "1px solid rgba(0,0,0,0.35)",
                    borderRadius: 3,
                    boxSizing: "border-box",
                    padding: 1.4,
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                      background: "#000",
                      borderRadius: 1.5,
                    }}
                  />
                </span>
                <span
                  style={{
                    width: 1.5,
                    height: 4,
                    background: "rgba(0,0,0,0.35)",
                    borderRadius: "0 1.5px 1.5px 0",
                  }}
                />
              </span>
            </span>
          </div>
          {/* Header */}
          <div
            style={{
              flexShrink: 0,
              padding: "4px 18px 11px",
              background: ld("#fff", "#181b1f"),
            }}
          >
            <div
              style={{
                fontSize: 19,
                fontWeight: 700,
                color: H.ink,
                letterSpacing: "-0.01em",
              }}
            >
              {t("dokumente")}
            </div>
            <div
              style={{
                fontSize: 10.5,
                color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
                marginTop: 2,
                lineHeight: 1.35,
              }}
            >
              {t("alleFuerFreigegebenenDateien")}
            </div>
          </div>
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 16px 11px",
              background: ld("#fff", "#181b1f"),
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontFamily: H.label,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: ld("rgba(11,15,25,0.38)", "rgba(250,250,250,0.38)"),
              }}
            >
              {t("k4Dateien")}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: 3,
                background: ld("#F1F2F4", "#2b2e32"),
                borderRadius: 8,
                gap: 2,
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 24,
                  borderRadius: 6,
                  background: ld("#fff", "#181b1f"),
                  boxShadow: `0 1px 2px ${ld("rgba(11,15,25,0.1)", "rgba(0,0,0,0.2)")}`,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: H.accent,
                }}
              >
                <LayoutGrid width={14} height={14} strokeWidth={2} />
              </span>
              <span
                style={{
                  width: 26,
                  height: 24,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
                }}
              >
                <List width={14} height={14} strokeWidth={2} />
              </span>
            </span>
          </div>
          {/* Doc grid */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              padding: "0 13px",
              background: ld("#fff", "#181b1f"),
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2,1fr)",
                gap: 10,
              }}
            >
              <DocTile
                variant="table"
                headWidth="52%"
                label={t("nebenkosten2024")}
              />
              <DocTile
                variant="lines"
                headWidth="58%"
                label={t("mietvertrag")}
              />
              <DocTile
                variant="lines"
                headWidth="48%"
                label={t("hausordnung")}
              />
              <DocTile
                variant="lines"
                headWidth="54%"
                label={t("uebergabeprotokoll")}
              />
            </div>
          </div>
          {/* Bottom tab bar */}
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-evenly",
              gap: 4,
              padding: "9px 14px 6px",
              borderTop: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
              background: ld("#fff", "#181b1f"),
              boxShadow: `0 -5px 16px -10px ${ld("rgba(11,15,25,0.2)", "rgba(0,0,0,0.35)")}`,
            }}
          >
            <TabItem
              icon={<Inbox width={21} height={21} strokeWidth={1.8} />}
              label={t("vorgaenge")}
            />
            <TabItem
              icon={<Megaphone width={21} height={21} strokeWidth={1.8} />}
              label={t("meldungen")}
            />
            <TabItem
              icon={<Folder width={20} height={20} strokeWidth={2} />}
              label={t("dokumente")}
              active
            />
          </div>
          <div
            style={{
              flexShrink: 0,
              height: 22,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              paddingBottom: 8,
              background: ld("#fff", "#181b1f"),
            }}
          >
            <span
              style={{
                width: 115,
                height: 5,
                borderRadius: 100,
                background: ld("#0B0F19", "#fafafa"),
                opacity: 0.82,
              }}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
